import React, { memo } from 'react';

import Button from 'react-bootstrap/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import i18n from '../messages.json';

/**
 * Compose / Preview modal footer.
 *
 * Compose mode (WWW-15059):  Cancel | Save Draft | Send Test | Preview
 * Preview mode:              Back to edit | Save Draft | Send Later | Send Now
 *
 * - Cancel / Back to edit anchors left via `.notification-compose__footer-cancel`.
 * - Save Draft uses `outline-secondary`; Send Test / Send Later use `outline-primary`;
 *   the primary action (Preview / Send Now) uses `primary`.
 * - Send Later opens the datetime popup (ScheduleDialog); Send Now sends immediately.
 * - Disabled buttons get `cursor: not-allowed` via the dialog-scoped CSS rule.
 *
 * Props
 *   isRenderPreview: boolean
 *   isEditing:       boolean — `currentMessage.id` truthy → button label is "Update Draft"
 *   formIsValid:     boolean
 *   isArticleValid:  boolean
 *   onCancel:        () => void — compose-mode Cancel
 *   onExitPreview:   () => void — preview-mode Back to edit
 *   onSave:          () => void — Save Draft / Update Draft
 *   onSendTest:      () => void — compose-mode Send Test (opens SendTestDialog)
 *   onPreview:       () => void — compose-mode Preview
 *   onSendLater:     () => void — preview-mode Send Later (opens ScheduleDialog)
 *   onSend:          () => void — preview-mode Send Now (immediate)
 */
const ComposeFooter = ({
  isRenderPreview,
  isEditing,
  formIsValid,
  isArticleValid,
  sendTestDisabled,
  onCancel,
  onExitPreview,
  onSave,
  onSendTest,
  onPreview,
  onSendLater,
  onSend,
}) => {
  const submitDisabled = !formIsValid || !isArticleValid;
  // Send Test is independent of campaign/segment selection — gate it only on
  // valid push content, falling back to the full form check when not provided.
  const sendTestBtnDisabled = sendTestDisabled ?? submitDisabled;

  const saveButton = (
    <Button variant='outline-secondary' disabled={submitDisabled} onClick={onSave}>
      {isEditing
        ? i18n.ink.notification.buttons.update
        : i18n.ink.notification.buttons.save}
    </Button>
  );

  if (isRenderPreview) {
    return (
      <>
        <Button
          variant='link'
          onClick={onExitPreview}
          className='notification-compose__footer-cancel notification-compose__footer-back'
        >
          {i18n.ink.notification.buttons.backToEdit}
        </Button>
        {saveButton}
        <Button variant='outline-primary' disabled={submitDisabled} onClick={onSendLater}>
          {i18n.ink.notification.buttons.sendLater}
        </Button>
        <Button variant='primary' disabled={submitDisabled} onClick={onSend}>
          {i18n.ink.notification.buttons.send}
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        variant='light'
        onClick={onCancel}
        className='notification-compose__footer-cancel'
      >
        {i18n.ink.notification.buttons.cancel}
      </Button>
      {saveButton}
      <Button variant='outline-primary' disabled={sendTestBtnDisabled} onClick={onSendTest}>
        <FontAwesomeIcon icon={faPaperPlane} className='mr-2' />
        <span className='ml-1'>{i18n.ink.notification.buttons.sendTest}</span>
      </Button>
      <Button variant='primary' disabled={submitDisabled} onClick={onPreview}>
        {i18n.ink.notification.buttons.preview}
      </Button>
    </>
  );
};

export default memo(ComposeFooter);
